import { CashApyDisclosure } from '@fruitful/compliance';
import { type HolisticSummaryCategory, Provider } from '@fruitful/types';
import Clipboard from '@react-native-clipboard/clipboard';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { capitalize, isEmpty } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import {
    ArrowRightIcon,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChevronDownIcon,
    CopyIcon,
    DEFAULT_COLOR,
    DEFAULT_ICON,
    EditIcon,
    EyeIcon,
    FruitfulCustomIcon,
    FullScreenSpinner,
    Icon,
    type IconType,
    InfoRow,
    LabelRegular,
    Notice,
    RowList,
    ScreenContainer,
    SPACE_COLORS,
    SPACE_ICONS,
    SuperscriptText,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    canRequestCard,
    formatInterestRateOrYieldPercent,
    getDefaultedAccountNickname,
    getFruitfulProductNameFromAccountType,
    getReadableAccountType,
    maskValue,
    preferLegalName,
} from '../../../common/utils/bankingUtils';
import { getNonTerminatedCards } from '../../../common/utils/cardUtils';
import {
    formatFullName,
    getPartnerDisplayFullName,
} from '../../../common/utils/userUtils';
import useGetHolisticSummary from '../../../holistic/hooks/useGetHolisticSummary';
import useRecategorizeAccount from '../../../holistic/hooks/useRecategorizeAccount';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import {
    captureException,
    captureMessage,
    captureWarning,
} from '../../../logger/Logger';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import useSendJointAccountHolderInvite from '../../hooks/useSendJointAccountHolderInvite';
import useUpdateBankAccount from '../../hooks/useUpdateBankAccount';
import { preselectedAccountForCardAtom } from '../../state/SpacesAtoms';
import {
    AccountType,
    type BankAccountDisplayDetail,
    RelationshipStatus,
} from '../../types/BankAccount';
import { type Card, CardForm } from '../../types/Card';
import CardSummaries from '../cards/CardSummaries';
import InviteSheet from '../joint-account-holder/InviteSheet';
import LegacySavingsInviteSheet from '../joint-account-holder/LegacySavingsInviteSheet';
import EditCategorySheet from './create-edit/edit-sheets/EditCategorySheet';
import EditColorSheet from './create-edit/edit-sheets/EditColorSheet';
import EditColorWarningSheet from './create-edit/edit-sheets/EditColorWarningSheet';
import EditIconSheet from './create-edit/edit-sheets/EditIconSheet';
import EditNicknameSheet from './create-edit/edit-sheets/EditNicknameSheet';

interface AccountUpdateDetails {
    category?: HolisticSummaryCategory;
    nickname?: string;
    icon?: string;
    color?: string;
}

const AccountDetails = () => {
    LogRocketFacade.tagPage('AccountDetails');

    const { id: userId, partnerMember, syncteraPersonId } = useAuthedUser();

    const toast = useToastController();

    const { syncteraAccountId } = useLocalSearchParams<{
        syncteraAccountId: string;
    }>();

    const [hideAccountDetails, setHideAccountDetails] = useState(true);
    const [isEditCategorySheetOpen, setIsEditCategorySheetOpen] =
        useState(false);
    const [isEditNicknameSheetOpen, setIsEditNicknameSheetOpen] =
        useState(false);
    const [isEditColorSheetOpen, setIsEditColorSheetOpen] = useState(false);
    const [isEditIconSheetOpen, setIsEditIconSheetOpen] = useState(false);
    const [isEditColorWarningSheetOpen, setIsEditColorWarningSheetOpen] =
        useState(false);
    const [isInviteSheetOpen, setIsInviteSheetOpen] = useState(false);

    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    const formattedAPY = formatInterestRateOrYieldPercent(account?.apy ?? 0);

    const {
        refetchAccountDetails,
        isLoading: isLoadingBankAccountDetails,
        canRequestPhysicalCard,
    } = useGetAllBankAccountDetails(false);

    const { refetch: refetchHolisticSummary } = useGetHolisticSummary(false);

    const {
        mutateAsync: recategorizeAccount,
        isPending: isRecategorizePending,
    } = useRecategorizeAccount();

    const { mutateAsync: updateAccount, isPending: isUpdateAccountPending } =
        useUpdateBankAccount();

    const {
        mutateAsync: sendJointInvite,
        isPending: isSendJointInvitePending,
    } = useSendJointAccountHolderInvite();

    const updateAccountAndRefetchDetails = async ({
        category,
        nickname,
        icon,
        color,
    }: AccountUpdateDetails) => {
        if (account == null) return;

        try {
            if (nickname != null || icon != null || color != null) {
                await updateAccount({
                    urlParams: { bankAccountId: account.bankAccountId },
                    body: {
                        syncteraAccountId: account.syncteraAccountId,
                        ...(nickname != null ? { nickname } : {}),
                        ...(icon != null ? { icon } : {}),
                        ...(color != null ? { color } : {}),
                    },
                });

                if (
                    nickname != null &&
                    account.chargeSecuredSyncteraAccountId != null
                ) {
                    await updateAccount({
                        urlParams: { bankAccountId: account.bankAccountId },
                        body: {
                            syncteraAccountId:
                                account.chargeSecuredSyncteraAccountId,
                            nickname: `Card - ${nickname}`,
                        },
                    });
                }
            } else if (category != null) {
                await recategorizeAccount({
                    body: {
                        accountId: account.syncteraAccountId,
                        holisticCategory: category,
                        provider: Provider.SYNCTERA,
                    },
                });
            }
            void refetchAccountDetails();
            void refetchHolisticSummary();
            toast.show('Account updated!');
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong updating your account');
        }
    };

    if (syncteraAccountId == null) {
        captureMessage('Space not found for details page');
        return <Redirect href="/home/banking" />;
    }

    if (
        account == null ||
        isLoadingBankAccountDetails ||
        isUpdateAccountPending ||
        isRecategorizePending
    ) {
        return <FullScreenSpinner />;
    }

    const {
        bankAccountId,
        accountNumber,
        routingNumber,
        accountType,
        primaryUser,
        jointUser,
        jointUserRelationshipStatus,
        holisticCategory,
        color,
        icon,
        cards,
    } = account;

    const inviteJointAccountHolder = async () => {
        if (partnerMember == null) return;

        try {
            await sendJointInvite({
                body: {
                    fromUserId: userId,
                    toEmail: partnerMember.email,
                    bankAccountId,
                },
            });
            toast.show('Joint account holder invited!');
        } catch (e) {
            captureException(e);
            toast.show(
                'Something went wrong inviting your joint account holder.',
            );
        } finally {
            setIsInviteSheetOpen(false);
        }
    };

    const hasVirtualCard =
        getNonTerminatedCards(cards).filter(
            (card) => card.form === CardForm.VIRTUAL,
        ).length > 0;
    const nickname = getDefaultedAccountNickname(account);
    const isJoint = jointUserRelationshipStatus === RelationshipStatus.ACTIVE;
    const isJointInvitePending =
        jointUserRelationshipStatus === RelationshipStatus.PENDING;

    const canEditSavingsAccountOwnership =
        accountType === AccountType.SAVING &&
        !isJoint; /* The legacy savings invite should appear if the member is on either a solo or joint membership */

    const canEditCashAccountOwnership =
        accountType === AccountType.CHECKING &&
        !isJoint &&
        /* TODO: we should remove partnerMember != null and explain that if you're on a solo membership you can't transition FF Cash to a joint account */
        partnerMember != null;

    const canEditOwnership =
        canEditSavingsAccountOwnership || canEditCashAccountOwnership;

    const accountNumberMasked = maskValue({
        value: accountNumber,
        maskedLength: accountNumber.length - 4,
        maskedCharacter: '•',
    });

    const copy = (value: string) => {
        Clipboard.setString(value);
        toast.show('Copied!', { duration: 1000 });
    };

    const accountColor = (
        <View
            backgroundColor={SPACE_COLORS[color ?? DEFAULT_COLOR]}
            borderRadius={100}
            height={22}
            width={22}
        />
    );
    const accountIcon = (
        <Icon
            icon={SPACE_ICONS[icon ?? DEFAULT_ICON] as IconType}
            size={20}
            color="$black"
        />
    );

    return (
        <ScreenContainer title="Account details" hasBackButton>
            <YStack gap={16}>
                {userId === primaryUser.id && isJointInvitePending && (
                    <Notice
                        variant="neutral"
                        title="Invitation pending"
                        description={`An invitation has been sent to ${getPartnerDisplayFullName(
                            partnerMember?.firstName,
                            partnerMember?.lastName,
                            'your partner',
                        )}`}
                    />
                )}
                <RowList
                    rowHeaderProps={{
                        title: nickname,
                        subtitle: `${getFruitfulProductNameFromAccountType(accountType)} account`,
                        leftContentOverride: (
                            <FruitfulCustomIcon
                                icon={icon}
                                color={color}
                                size="sm"
                            />
                        ),
                        primaryButton: {
                            icon: EyeIcon,
                            onPress: () => {
                                setHideAccountDetails(!hideAccountDetails);
                            },
                        },
                    }}
                >
                    <InfoRow
                        title="Account number"
                        value={
                            hideAccountDetails
                                ? accountNumberMasked
                                : accountNumber
                        }
                        trailingIcon={CopyIcon}
                        onPress={() => {
                            copy(accountNumber);
                        }}
                        isPrivate
                    />
                    <InfoRow
                        title="Routing number"
                        value={routingNumber}
                        trailingIcon={CopyIcon}
                        onPress={() => {
                            copy(routingNumber);
                        }}
                    />
                    <InfoRow
                        title="Account type"
                        value={getReadableAccountType(accountType)}
                    />
                    <InfoRow
                        title="Ownership"
                        value={
                            isJoint
                                ? 'Joint'
                                : isJointInvitePending
                                  ? 'Individual (Invitation pending)'
                                  : 'Individual'
                        }
                        trailingIcon={canEditOwnership ? EditIcon : undefined}
                        onPress={
                            canEditOwnership
                                ? () => {
                                      setIsInviteSheetOpen(true);
                                  }
                                : undefined
                        }
                        valueProps={
                            isJointInvitePending && {
                                maxWidth: 'auto',
                            }
                        }
                    />
                    <InfoRow
                        title="Primary account holder"
                        value={formatFullName(...preferLegalName(primaryUser))}
                    />
                    {jointUser != null && !isJointInvitePending && (
                        <InfoRow
                            title="Account holder"
                            value={formatFullName(
                                ...preferLegalName(jointUser),
                            )}
                        />
                    )}
                    <InfoRow
                        title="Annual Percentage Yield"
                        value={
                            <SuperscriptText
                                textBefore={formattedAPY}
                                superscript={1}
                                onInvalidSuperscriptText={captureWarning}
                            />
                        }
                    />
                </RowList>

                {!isEmpty(cards) && (
                    <RowList
                        rowHeaderProps={{
                            title: 'Card',
                            rightContent: (
                                <XStack
                                    alignItems="center"
                                    gap={4}
                                    onPress={() => {
                                        router.navigate({
                                            pathname:
                                                '/banking/card/manage-card',
                                            params: { syncteraAccountId },
                                        });
                                    }}
                                    cursor="pointer"
                                >
                                    <LabelRegular color="$gray1000">
                                        Manage card
                                    </LabelRegular>
                                    <Icon
                                        icon={ArrowRightIcon}
                                        size={14}
                                        color="$gray1000"
                                    />
                                </XStack>
                            ),
                        }}
                    >
                        <View
                            padding={16}
                            alignItems={
                                (cards?.length ?? 0) > 1
                                    ? 'flex-start'
                                    : 'center'
                            }
                        >
                            <CardSummaries
                                syncteraAccountId={syncteraAccountId}
                                cards={cards as Card[]}
                            />
                        </View>
                    </RowList>
                )}

                <RowList
                    rowHeaderProps={{
                        title: 'Customizations',
                    }}
                >
                    <InfoRow
                        title="Nickname"
                        value={nickname}
                        trailingIcon={EditIcon}
                        onPress={() => {
                            setIsEditNicknameSheetOpen(true);
                        }}
                    />
                    <InfoRow
                        title="Categorized as"
                        value={capitalize(holisticCategory)}
                        trailingIcon={ChevronDownIcon}
                        onPress={() => {
                            setIsEditCategorySheetOpen(true);
                        }}
                    />
                    <InfoRow
                        title="Color"
                        value={accountColor}
                        trailingIcon={ChevronDownIcon}
                        onPress={() => {
                            if (hasVirtualCard) {
                                setIsEditColorWarningSheetOpen(true);
                            } else {
                                setIsEditColorSheetOpen(true);
                            }
                        }}
                    />
                    <InfoRow
                        title="Icon"
                        value={accountIcon}
                        trailingIcon={ChevronDownIcon}
                        onPress={() => {
                            setIsEditIconSheetOpen(true);
                        }}
                    />
                </RowList>
                {canRequestCard(syncteraPersonId, account) && (
                    <RequestCard
                        account={account}
                        canRequestPhysicalCard={canRequestPhysicalCard}
                    />
                )}
                <YStack paddingBottom={24} paddingHorizontal={8}>
                    <CashApyDisclosure
                        superscript={1}
                        color="$gray900"
                        titleColor="$gray1000"
                    />
                </YStack>
            </YStack>

            <EditCategorySheet
                isOpen={isEditCategorySheetOpen}
                onClose={() => {
                    setIsEditCategorySheetOpen(false);
                }}
                onSelectCategory={(category) => {
                    void updateAccountAndRefetchDetails({ category });
                    setIsEditCategorySheetOpen(false);
                }}
                selectedCategory={
                    // Convert database upper-cased category enum to lower-cased version used in holistic domain
                    holisticCategory.toLowerCase() as HolisticSummaryCategory
                }
            />
            {isEditNicknameSheetOpen && (
                <EditNicknameSheet
                    onClose={() => {
                        setIsEditNicknameSheetOpen(false);
                    }}
                    onSubmit={(nickname) => {
                        void updateAccountAndRefetchDetails({ nickname });
                        setIsEditNicknameSheetOpen(false);
                    }}
                    nickname={nickname}
                />
            )}
            <EditColorSheet
                isOpen={isEditColorSheetOpen}
                onClose={() => {
                    setIsEditColorSheetOpen(false);
                }}
                onSubmit={(color: string) => {
                    // Send both color and icon together: they cannot be updated independently because Synctera :(
                    void updateAccountAndRefetchDetails({
                        color,
                        icon: icon ?? DEFAULT_ICON,
                    });
                    setIsEditColorSheetOpen(false);
                }}
                icon={icon ?? DEFAULT_ICON}
                selectedColor={color ?? DEFAULT_COLOR}
            />
            <EditIconSheet
                isOpen={isEditIconSheetOpen}
                onClose={() => {
                    setIsEditIconSheetOpen(false);
                }}
                onSubmit={(icon: string) => {
                    // Send both color and icon together: they cannot be updated independently because Synctera :(
                    void updateAccountAndRefetchDetails({
                        icon,
                        color: color ?? DEFAULT_COLOR,
                    });
                    setIsEditIconSheetOpen(false);
                }}
                selectedIcon={icon ?? DEFAULT_ICON}
            />
            {isEditColorWarningSheetOpen && (
                <EditColorWarningSheet
                    isOpen={isEditColorWarningSheetOpen}
                    onClose={() => {
                        setIsEditColorWarningSheetOpen(false);
                    }}
                    onPress={() => {
                        setIsEditColorWarningSheetOpen(false);
                        setIsEditColorSheetOpen(true);
                    }}
                />
            )}
            {canEditCashAccountOwnership && partnerMember?.email != null && (
                <InviteSheet
                    isOpen={isInviteSheetOpen}
                    onClose={() => {
                        setIsInviteSheetOpen(false);
                    }}
                    confirmButtonDisabled={isSendJointInvitePending}
                    onConfirm={() => {
                        void inviteJointAccountHolder();
                        void updateAccountAndRefetchDetails({});
                    }}
                    partnerMemberFullName={getPartnerDisplayFullName(
                        partnerMember.firstName,
                        partnerMember.lastName,
                    )}
                    partnerMemberEmail={partnerMember.email}
                />
            )}
            {canEditSavingsAccountOwnership && (
                <LegacySavingsInviteSheet
                    isOpen={isInviteSheetOpen}
                    onClose={() => {
                        setIsInviteSheetOpen(false);
                    }}
                />
            )}
        </ScreenContainer>
    );
};

interface RequestCardProps {
    account: BankAccountDisplayDetail;
    canRequestPhysicalCard: boolean;
}

const RequestCard = ({ account, canRequestPhysicalCard }: RequestCardProps) => {
    const setPreselectedAccountForCardAtom = useSetAtom(
        preselectedAccountForCardAtom,
    );

    return (
        <RowList
            rowHeaderProps={{
                title: 'Card',
                rightContent: (
                    <XStack
                        alignItems="center"
                        gap={4}
                        onPress={() => {
                            router.navigate('/settings/faq');
                        }}
                        cursor="pointer"
                    >
                        <LabelRegular color="$gray1000">
                            Learn more
                        </LabelRegular>
                        <Icon
                            icon={ArrowRightIcon}
                            size={14}
                            color="$gray1000"
                        />
                    </XStack>
                ),
            }}
        >
            <YStack gap={16} padding={16}>
                <BodyMediumRegular color="$gray1000">
                    {canRequestPhysicalCard
                        ? 'There are no cards attached to this account, but you can add a Fruitful Card at any time.'
                        : 'There are no cards attached to this account, but you can request a virtual card at any time.'}
                </BodyMediumRegular>
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    text={
                        canRequestPhysicalCard
                            ? 'Add a Fruitful Card'
                            : 'Request a virtual card'
                    }
                    onPress={() => {
                        setPreselectedAccountForCardAtom(account);
                        router.navigate('/banking/onboarding/add-card');
                    }}
                    buttonProps={{
                        width: 'auto',
                    }}
                />
            </YStack>
        </RowList>
    );
};

export default AccountDetails;
